import React, { useState, useEffect } from 'react';
import { X, FileText } from 'lucide-react';
import { CreditReportOrder } from '../../types';
import { useToast } from '../../contexts/ToastContext';

interface CreditReportOrderWidgetProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
  orderedBy: string;
  orderedFrom: 'home' | 'credit_management' | 'cybersweep' | 'credit_block';
  onSubmit: (order: CreditReportOrder) => Promise<void>;
}

const CreditReportOrderWidget: React.FC<CreditReportOrderWidgetProps> = ({
  isOpen,
  onClose,
  clientId,
  clientName,
  orderedBy,
  orderedFrom,
  onSubmit
}) => {
  const { warning, error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [orderForm, setOrderForm] = useState<Partial<CreditReportOrder>>({
    clientId,
    clientName,
    clientLookup: clientName,
    firstName: '',
    lastName: '',
    birthDate: '',
    currentAddress: '',
    previousAddress: '',
    currentEmployer: '',
    previousEmployer: '',
    bureau: 'equifax',
    creditScore: undefined,
    alias: '',
    grade: '',
    rating: '',
    description: '',
    creditReportPullDate: '',
    productCode: '',
    reportKey: '',
    minimumMonthlyPayment: undefined,
    totalCreditors: undefined,
    realEstate: undefined,
    goodStanding: undefined,
    installment: undefined,
    openAccounts: undefined,
    lineOfCredit: undefined,
    closedAccounts: undefined,
    totalDebts: undefined,
    creditRepositoryIncluded: [],
    autoCreditors: undefined,
    mortgageCreditors: undefined,
    creditCardCreditors: undefined,
    otherCreditors: undefined,
    creditReportStatus: 'success',
    creditReportFailure: '',
    newFilesAttachedDateTime: '',
    totalFilesAttached: 0,
    reportType: 'standard',
    orderedBy,
    orderedFrom,
    purpose: 'manual_entry',
    notes: ''
  });

  // Auto-fill client information on mount
  useEffect(() => {
    if (isOpen && clientName) {
      // Split client name into first and last name
      const nameParts = clientName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setOrderForm(prev => ({
        ...prev,
        clientId,
        clientName,
        clientLookup: clientName,
        firstName,
        lastName
      }));
    }
  }, [isOpen, clientId, clientName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orderForm.clientLookup?.trim() || !orderForm.firstName?.trim() || !orderForm.lastName?.trim() || !orderForm.bureau) {
      warning('Missing required fields', 'Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit(orderForm as CreditReportOrder);
      onClose();
      // Reset form
      setOrderForm({
        clientId,
        clientName,
        clientLookup: clientName,
        firstName: '',
        lastName: '',
        birthDate: '',
        currentAddress: '',
        previousAddress: '',
        currentEmployer: '',
        previousEmployer: '',
        bureau: 'equifax',
        creditScore: undefined,
        alias: '',
        grade: '',
        rating: '',
        description: '',
        creditReportPullDate: '',
        productCode: '',
        reportKey: '',
        minimumMonthlyPayment: undefined,
        totalCreditors: undefined,
        realEstate: undefined,
        goodStanding: undefined,
        installment: undefined,
        openAccounts: undefined,
        lineOfCredit: undefined,
        closedAccounts: undefined,
        totalDebts: undefined,
        creditRepositoryIncluded: [],
        autoCreditors: undefined,
        mortgageCreditors: undefined,
        creditCardCreditors: undefined,
        otherCreditors: undefined,
        creditReportStatus: 'success',
        creditReportFailure: '',
        newFilesAttachedDateTime: '',
        totalFilesAttached: 0,
        reportType: 'standard',
        orderedBy,
        orderedFrom,
        purpose: 'manual_entry',
        notes: ''
      });
    } catch (error) {
      console.error('Failed to submit credit report order:', error);
      showError('Failed to submit order', 'Please try again');
    } finally {
      setIsLoading(false);
    }
  };

  const inputCls =
    'w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100';
  const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
  const toNumber = (value: string): number | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : undefined;
  };
  const toggleRepoIncluded = (key: string) => {
    const current = orderForm.creditRepositoryIncluded ?? [];
    const next = current.includes(key)
      ? current.filter((v) => v !== key)
      : [...current, key];
    setOrderForm({ ...orderForm, creditRepositoryIncluded: next });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center space-x-3">
            <FileText className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              New Credit Report
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 max-h-[70vh] overflow-y-auto">
          <div className="space-y-6">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Client Lookup *</label>
                  <input
                    type="text"
                    value={orderForm.clientLookup ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, clientLookup: e.target.value, clientName: e.target.value })}
                    className={inputCls}
                    required
                  />
                </div>
                <div>
                  <label className={labelCls}>First Name *</label>
                  <input
                    type="text"
                    value={orderForm.firstName}
                    onChange={(e) => setOrderForm({ ...orderForm, firstName: e.target.value })}
                    className={inputCls}
                    required
                  />
                </div>
                <div>
                  <label className={labelCls}>Last Name *</label>
                  <input
                    type="text"
                    value={orderForm.lastName}
                    onChange={(e) => setOrderForm({ ...orderForm, lastName: e.target.value })}
                    className={inputCls}
                    required
                  />
                </div>
                <div>
                  <label className={labelCls}>Bureau *</label>
                  <select
                    value={orderForm.bureau}
                    onChange={(e) => setOrderForm({ ...orderForm, bureau: e.target.value as CreditReportOrder['bureau'] })}
                    className={inputCls}
                  >
                    <option value="equifax">Equifax</option>
                    <option value="experian">Experian</option>
                    <option value="transunion">Transunion</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Credit Score</label>
                  <input
                    type="number"
                    value={orderForm.creditScore ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, creditScore: toNumber(e.target.value) })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Alias</label>
                  <input
                    type="text"
                    value={orderForm.alias ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, alias: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Grade</label>
                  <input
                    type="text"
                    value={orderForm.grade ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, grade: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Rating</label>
                  <input
                    type="text"
                    value={orderForm.rating ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, rating: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Birth Date</label>
                  <input
                    type="date"
                    value={orderForm.birthDate}
                    onChange={(e) => setOrderForm({ ...orderForm, birthDate: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Current Address</label>
                  <input
                    type="text"
                    value={orderForm.currentAddress}
                    onChange={(e) => setOrderForm({ ...orderForm, currentAddress: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Previous Address</label>
                  <input
                    type="text"
                    value={orderForm.previousAddress ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, previousAddress: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Current Employer</label>
                  <input
                    type="text"
                    value={orderForm.currentEmployer ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, currentEmployer: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Previous Employer</label>
                  <input
                    type="text"
                    value={orderForm.previousEmployer ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, previousEmployer: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Description</label>
                  <input
                    type="text"
                    value={orderForm.description ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, description: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Credit Report Pull Date</label>
                  <input
                    type="datetime-local"
                    value={orderForm.creditReportPullDate ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, creditReportPullDate: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Product Code</label>
                  <input
                    type="text"
                    value={orderForm.productCode ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, productCode: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Report Key</label>
                  <input
                    type="text"
                    value={orderForm.reportKey ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, reportKey: e.target.value })}
                    className={inputCls}
                  />
                </div>
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                  <label className={labelCls}>Minimum Monthly</label>
                  <input type="number" value={orderForm.minimumMonthlyPayment ?? ''} onChange={(e) => setOrderForm({ ...orderForm, minimumMonthlyPayment: toNumber(e.target.value) })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Total Creditors</label>
                  <input type="number" value={orderForm.totalCreditors ?? ''} onChange={(e) => setOrderForm({ ...orderForm, totalCreditors: toNumber(e.target.value) })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Real Estate</label>
                  <input type="number" value={orderForm.realEstate ?? ''} onChange={(e) => setOrderForm({ ...orderForm, realEstate: toNumber(e.target.value) })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Good Standing</label>
                  <input type="number" value={orderForm.goodStanding ?? ''} onChange={(e) => setOrderForm({ ...orderForm, goodStanding: toNumber(e.target.value) })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Installment</label>
                  <input type="number" value={orderForm.installment ?? ''} onChange={(e) => setOrderForm({ ...orderForm, installment: toNumber(e.target.value) })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Open Accounts</label>
                  <input type="number" value={orderForm.openAccounts ?? ''} onChange={(e) => setOrderForm({ ...orderForm, openAccounts: toNumber(e.target.value) })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Line of Credit</label>
                  <input type="number" value={orderForm.lineOfCredit ?? ''} onChange={(e) => setOrderForm({ ...orderForm, lineOfCredit: toNumber(e.target.value) })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Closed Accounts</label>
                  <input type="number" value={orderForm.closedAccounts ?? ''} onChange={(e) => setOrderForm({ ...orderForm, closedAccounts: toNumber(e.target.value) })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Total Debts</label>
                  <input type="number" value={orderForm.totalDebts ?? ''} onChange={(e) => setOrderForm({ ...orderForm, totalDebts: toNumber(e.target.value) })} className={inputCls} />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Credit Repository Included</label>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-800 dark:text-gray-200">
                    {['Experian', 'Equifax', 'TransUnion'].map((repo) => (
                      <label key={repo} className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={(orderForm.creditRepositoryIncluded ?? []).includes(repo)}
                          onChange={() => toggleRepoIncluded(repo)}
                        />
                        {repo}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">Creditors</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Auto</label>
                  <input type="number" value={orderForm.autoCreditors ?? ''} onChange={(e) => setOrderForm({ ...orderForm, autoCreditors: toNumber(e.target.value) })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Mortgages</label>
                  <input type="number" value={orderForm.mortgageCreditors ?? ''} onChange={(e) => setOrderForm({ ...orderForm, mortgageCreditors: toNumber(e.target.value) })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Credit Cards</label>
                  <input type="number" value={orderForm.creditCardCreditors ?? ''} onChange={(e) => setOrderForm({ ...orderForm, creditCardCreditors: toNumber(e.target.value) })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Others</label>
                  <input type="number" value={orderForm.otherCreditors ?? ''} onChange={(e) => setOrderForm({ ...orderForm, otherCreditors: toNumber(e.target.value) })} className={inputCls} />
                </div>
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">System Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Credit Report Status</label>
                  <select
                    value={orderForm.creditReportStatus ?? 'success'}
                    onChange={(e) => setOrderForm({ ...orderForm, creditReportStatus: e.target.value as 'success' | 'failure' })}
                    className={inputCls}
                  >
                    <option value="success">Success</option>
                    <option value="failure">Error</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Credit Report Failure</label>
                  <input
                    type="text"
                    value={orderForm.creditReportFailure ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, creditReportFailure: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>New Files Attached Date/Time</label>
                  <input
                    type="datetime-local"
                    value={orderForm.newFilesAttachedDateTime ?? ''}
                    onChange={(e) => setOrderForm({ ...orderForm, newFilesAttachedDateTime: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Total Files Attached</label>
                  <input
                    type="number"
                    value={orderForm.totalFilesAttached ?? 0}
                    onChange={(e) => setOrderForm({ ...orderForm, totalFilesAttached: toNumber(e.target.value) ?? 0 })}
                    className={inputCls}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className={labelCls}>Notes</label>
              <textarea
                value={orderForm.notes}
                onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
                rows={3}
                className={inputCls}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-6 py-2 rounded-lg font-medium flex items-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Create Credit Report
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreditReportOrderWidget;